import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Page, AppHeader, PageBody } from '../components/Layout';

const API = 'https://axis-backend-production-5e9b.up.railway.app';

const EMOTION_ORDER = ['Fear', 'Guilt', 'Shame', 'Anger', 'Envy', 'Grief'];

const SOURCES = [
  'Mother', 'Father', 'Stepmother', 'Stepfather',
  'Grandmother', 'Grandfather', 'Brother', 'Sister',
  'Romantic Partner', 'Teacher', 'Boss', 'Custom'
];

const SOURCE_ORDER = SOURCES.reduce((acc, s, i) => { acc[s] = i; return acc; }, {});

const LIFE_PERIODS = [
  { label: 'Early Childhood', range: '0–7', order: 1 },
  { label: 'Childhood', range: '8–12', order: 2 },
  { label: 'Adolescence', range: '13–17', order: 3 },
  { label: 'Early Adulthood', range: '18–25', order: 4 },
  { label: 'Adulthood', range: '26–40', order: 5 },
  { label: 'Mature Adulthood', range: '41–60', order: 6 },
  { label: 'Later Life', range: '60+', order: 7 },
];

const getPeriodOrder = (period) => {
  if (!period) return 999;
  const p = Array.isArray(period) ? period[0] : period;
  const found = LIFE_PERIODS.find(lp => lp.label === p);
  return found ? found.order : 999;
};

const getPeriodDisplay = (period) => {
  if (!period) return '';
  if (Array.isArray(period)) {
    if (period.length === 0) return '';
    if (period.length === 1) return period[0];
    return period[0] + ' → ' + period[period.length - 1];
  }
  return period;
};

const EMOTION_COLORS = {
  Fear:  { bg: 'rgba(101,67,33,0.35)',   border: 'rgba(139,90,43,0.6)',   text: '#C8A87A' },
  Guilt: { bg: 'rgba(180,150,0,0.25)',   border: 'rgba(210,180,0,0.5)',   text: '#D4C060' },
  Shame: { bg: 'rgba(180,80,0,0.28)',    border: 'rgba(210,100,0,0.5)',   text: '#E8955A' },
  Anger: { bg: 'rgba(176,40,40,0.28)',   border: 'rgba(200,60,60,0.5)',   text: '#E08080' },
  Envy:  { bg: 'rgba(100,40,160,0.28)',  border: 'rgba(130,60,190,0.5)',  text: '#B07ED4' },
  Grief: { bg: 'rgba(180,100,120,0.25)', border: 'rgba(200,130,145,0.5)', text: '#D4A0B0' },
};

const TREE_COLORS = {
  Fear:  { bg: 'rgba(139,90,60,0.15)',   border: 'rgba(139,90,60,0.5)',   dot: '#8B5A3C' },
  Guilt: { bg: 'rgba(180,160,60,0.15)',  border: 'rgba(180,160,60,0.5)',  dot: '#B4A03C' },
  Shame: { bg: 'rgba(200,120,50,0.15)',  border: 'rgba(200,120,50,0.5)',  dot: '#C87832' },
  Anger: { bg: 'rgba(176,90,90,0.15)',   border: 'rgba(176,90,90,0.5)',   dot: '#B05A5A' },
  Envy:  { bg: 'rgba(130,90,180,0.15)',  border: 'rgba(130,90,180,0.5)',  dot: '#825AB4' },
  Grief: { bg: 'rgba(160,120,130,0.15)', border: 'rgba(160,120,130,0.5)', dot: '#A07882' },
};
const DEFAULT_TREE_COLORS = { bg: 'rgba(142,196,224,0.08)', border: 'rgba(142,196,224,0.3)', dot: '#8EC4E0' };

const ArrowLeft = () => (
  <span style={{ fontSize: '10px', color: '#8BAFC8', marginLeft: '6px' }}>← <span style={{ fontStyle: 'italic', fontWeight: 400, letterSpacing: '1px', textTransform: 'none' }}>emotional input</span></span>
);
const ArrowRight = () => (
  <span style={{ fontSize: '10px', color: '#8BAFC8', marginLeft: '6px' }}>→ <span style={{ fontStyle: 'italic', fontWeight: 400, letterSpacing: '1px', textTransform: 'none' }}>emotional output</span></span>
);

const SEQUENCES = {
  trigger:   ['name','trigger','behaviors','thoughts','feelings','beliefs','burden','period','source','rootComplex','counter','counterBehavior'],
  behaviors: ['name','behaviors','trigger','thoughts','feelings','beliefs','burden','period','source','rootComplex','counter','counterBehavior'],
  burden:    ['name','burden','beliefs','thoughts','feelings','behaviors','trigger','period','source','rootComplex','counter','counterBehavior'],
  beliefs:   ['name','beliefs','thoughts','feelings','behaviors','trigger','burden','period','source','rootComplex','counter','counterBehavior'],
};

const STEP_CONFIGS = {
  name:            { label: 'Name', q: 'Give this pattern a name.', hint: 'A name you will recognize. It can be simple.', type: 'input' },
  trigger:         { label: 'Trigger', q: 'What triggered you? Describe what happened.', hint: 'It can be a situation, a word, a moment. Whatever activated this.', type: 'textarea' },
  behaviors:       { label: 'Behavior', q: 'How are you acting or reacting when this complex is triggered?', hint: 'What are you doing, avoiding, or compelled toward?', type: 'textarea' },
  thoughts:        { label: 'Thoughts', q: 'What thoughts are arising?', hint: 'What is the mind saying?', type: 'textarea' },
  feelings:        { label: 'Feelings', q: 'What do you feel as these thoughts arise?', hint: 'How does it make you feel?', type: 'textarea', optional: true },
  beliefs:         { label: 'Beliefs', q: 'What do you believe when this pattern is active?', hint: 'Explore what you believe while this complex is active.', type: 'textarea' },
  burden:          { label: 'Underlying Emotional Burden', q: 'What is the underlying emotion at the root of this?', hint: 'This is the emotional wound the complex is built around.', type: 'select' },
  period:          { label: 'Life Period', q: 'When in your life did this pattern first emerge?', hint: 'Optional. Select all that apply.', type: 'period', optional: true },
  source:          { label: 'Source', q: 'Who or what was the source of this pattern?', hint: 'Optional. The person or origin this complex formed around.', type: 'sourceSelect', optional: true },
  rootComplex:     { label: 'Root Complex', q: 'Does this pattern trace back to an earlier complex?', hint: 'Optional. Select primary root from dropdown. Add secondary roots below.', type: 'rootSelect', optional: true },
  counter:         { label: 'Counter Belief', q: 'What else could also be true?', hint: 'Optional. Give this belief an honest opposing voice.', type: 'textarea', optional: true },
  counterBehavior: { label: 'Counter Behavior', q: 'When that counter belief is active, how do you act differently?', hint: 'Optional. The conscious alternative to your pattern.', type: 'textarea', optional: true },
};

const ENTRY_POINTS = [
  { id: 'trigger',   label: 'Trigger',   desc: 'Something just happened. I know what set this off.' },
  { id: 'burden',    label: 'Emotion',   desc: 'I can name the emotion that is present right now.' },
  { id: 'behaviors', label: 'Behavior',  desc: 'I can see how I am acting or reacting right now.' },
  { id: 'beliefs',   label: 'Belief',    desc: 'I am aware of a deep rooted belief I carry.' },
];



function CollapsibleSection({ label, color, defaultOpen, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div style={{ marginTop: '20px' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          fontSize: '9px',
          fontWeight: '600',
          letterSpacing: '3px',
          textTransform: 'uppercase',
          color: color,
          marginBottom: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: '10px', display: 'inline-block', transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        {label}
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}

const ConcentricSquare = ({ size = 48, color = 'rgba(74,174,136,0.9)' }) => {
  const s = size;
  const rings = 5;
  const rects = [];
  for (let i = 0; i < rings; i++) {
    const pad = (i * s) / (rings * 2 + 1);
    const w = s - pad * 2;
    rects.push(<rect key={i} x={pad} y={pad} width={w} height={w} fill="none" stroke={color} strokeWidth="1.5" />);
  }
  return <svg width={s} height={s} viewBox={'0 0 ' + s + ' ' + s}>{rects}</svg>;
};

const RootComplexSelector = ({ value, onChange, availableRoots }) => {
  const selected = Array.isArray(value) ? value : [];
  const primary = selected[0] || '';
  const secondaries = selected.slice(1);
  const setPrimary = (name) => {
    const rest = secondaries.filter(n => n !== name);
    onChange(name ? [name, ...rest] : rest);
  };
  const toggleSecondary = (name) => {
    if (name === primary) return;
    const updated = secondaries.includes(name) ? secondaries.filter(n => n !== name) : [...secondaries, name];
    onChange(primary ? [primary, ...updated] : updated);
  };
  return (
    <div>
      <select style={styles.input} value={primary} onChange={e => setPrimary(e.target.value)}>
        <option value="">None / Unknown</option>
        {availableRoots.map((c, i) => <option key={i} value={c.name}>{c.name}{c.period ? ' · ' + getPeriodDisplay(c.period) : ''}</option>)}
      </select>
      {primary && availableRoots.filter(c => c.name !== primary).length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <div style={{ fontSize: '9px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#8BAFC8', marginBottom: '8px' }}>Secondary Roots <span style={{ fontStyle: 'italic', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>optional, draws dashed lines in tree</span></div>
          <select style={styles.input} value="" onChange={e => { if (e.target.value) toggleSecondary(e.target.value); }}>
            <option value="">Add secondary root...</option>
            {availableRoots.filter(c => c.name !== primary).map((c, i) => (
              <option key={i} value={c.name}>{secondaries.includes(c.name) ? '✓ ' : ''}{c.name}{c.period ? ' · ' + getPeriodDisplay(c.period) : ''}</option>
            ))}
          </select>
          {secondaries.length > 0 && secondaries.map((name, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: 'rgba(142,196,224,0.06)', border: '1px solid rgba(142,196,224,0.2)', borderRadius: '3px', marginTop: '6px' }}>
              <span style={{ fontSize: '12px', color: '#A0C4D8' }}>{name}</span>
              <button style={{ background: 'none', border: 'none', color: '#8BAFC8', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }} onClick={() => toggleSecondary(name)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function PersonPicker({ value, onChange, people, onAddPerson }) {
  const [adding, setAdding] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const sortedPeople = people.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    await onAddPerson(trimmed);
    onChange(trimmed);
    setNewName('');
    setAdding(false);
  };

  if (adding) {
    return (
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          style={{ ...styles.input, flex: 1 }}
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="Name of the person..."
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
        />
        <button type="button" style={styles.btn} onClick={handleAdd}>Add</button>
        <button type="button" style={styles.cancelBtn} onClick={() => { setAdding(false); setNewName(''); }}>Cancel</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <select style={{ ...styles.input, flex: 1 }} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">None / Unknown</option>
        {sortedPeople.map((p, i) => <option key={i} value={p.name}>{p.name}</option>)}
      </select>
      <button type="button" style={styles.cancelBtn} onClick={() => setAdding(true)}>+ New</button>
    </div>
  );
}

function ResolutionModal({ resolvedComplexes, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 20px' }}>
      <div style={{ background: '#162534', border: '1px solid rgba(74,174,136,0.4)', borderRadius: '4px', width: '100%', maxWidth: '520px', padding: '32px', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <ConcentricSquare size={36} color="rgba(74,174,136,0.8)" />
            <div>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', fontWeight: '300', color: '#4AAE88' }}>Resolution</div>
              <div style={{ fontSize: '10px', color: '#8BAFC8', letterSpacing: '2px', textTransform: 'uppercase', marginTop: '2px' }}>Pattern dissolved</div>
            </div>
          </div>
          <button style={{ background: 'none', border: 'none', color: '#8BAFC8', cursor: 'pointer', fontSize: '18px' }} onClick={onClose}>✕</button>
        </div>
        {resolvedComplexes.map((c, i) => (
          <div key={i} style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: i < resolvedComplexes.length - 1 ? '1px solid rgba(142,196,224,0.1)' : 'none' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#D8E6F0', marginBottom: '12px', fontFamily: 'Georgia, serif' }}>{c.name}</div>
            {c.resolution && (
              <>
                {c.resolution.what && <div style={{ marginBottom: '10px' }}><div style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', color: '#4AAE88', marginBottom: '4px' }}>What happened</div><div style={{ fontSize: '13px', color: '#A0C4D8', fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>{c.resolution.what}</div></div>}
                {c.resolution.counterBelief && <div style={{ marginBottom: '10px' }}><div style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', color: '#4AAE88', marginBottom: '4px' }}>Counter Belief Deployed</div><div style={{ fontSize: '13px', color: '#A0C4D8', fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>{c.resolution.counterBelief}</div></div>}
                {c.resolution.counterBehavior && <div style={{ marginBottom: '10px' }}><div style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', color: '#4AAE88', marginBottom: '4px' }}>Counter Behavior Deployed</div><div style={{ fontSize: '13px', color: '#A0C4D8', fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>{c.resolution.counterBehavior}</div></div>}
                {c.resolution.reflection && <div style={{ marginBottom: '10px' }}><div style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', color: '#4AAE88', marginBottom: '4px' }}>Reflection</div><div style={{ fontSize: '13px', color: '#A0C4D8', fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>{c.resolution.reflection}</div></div>}
                {c.resolution.period && <div style={{ fontSize: '10px', color: '#8BAFC8', marginTop: '6px', letterSpacing: '1px' }}>{c.resolution.period}</div>}
              </>
            )}
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button style={{ background: 'rgba(74,174,136,0.1)', border: '1px solid rgba(74,174,136,0.3)', borderRadius: '3px', padding: '10px 24px', color: '#4AAE88', fontSize: '11px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' }} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function ResolutionForm({ complex, onSave, onCancel }) {
  const [form, setForm] = useState({
    what: '', counterBelief: complex.counter || '', counterBehavior: complex.counterBehavior || '', reflection: '', period: complex.period || '',
  });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 20px' }}>
      <div style={{ background: '#162534', border: '1px solid rgba(74,174,136,0.3)', borderRadius: '4px', width: '100%', maxWidth: '520px', padding: '32px', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
          <ConcentricSquare size={32} color="rgba(74,174,136,0.7)" />
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', fontWeight: '300', color: '#4AAE88' }}>Mark as Resolved</div>
        </div>
        <div style={{ fontSize: '12px', color: '#8BAFC8', marginBottom: '28px', lineHeight: 1.6 }}>Document the moment this pattern was resolved. This becomes part of your map.</div>
        <div style={{ marginBottom: '20px' }}><label style={{ display: 'block', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#4AAE88', marginBottom: '8px' }}>What happened</label><textarea style={{ width: '100%', background: '#0f2236', border: '1px solid rgba(74,174,136,0.2)', borderRadius: '3px', padding: '10px 14px', color: '#D8E6F0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', minHeight: '80px' }} value={form.what} onChange={e => setForm({ ...form, what: e.target.value })} placeholder="Describe the moment or experience that resolved this pattern..." autoFocus /></div>
        <div style={{ marginBottom: '20px' }}><label style={{ display: 'block', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#4AAE88', marginBottom: '8px' }}>Counter Belief Deployed</label><textarea style={{ width: '100%', background: '#0f2236', border: '1px solid rgba(74,174,136,0.2)', borderRadius: '3px', padding: '10px 14px', color: '#D8E6F0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} value={form.counterBelief} onChange={e => setForm({ ...form, counterBelief: e.target.value })} placeholder="Which counter belief was active?" rows={2} /></div>
        <div style={{ marginBottom: '20px' }}><label style={{ display: 'block', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#4AAE88', marginBottom: '8px' }}>Counter Behavior Deployed</label><textarea style={{ width: '100%', background: '#0f2236', border: '1px solid rgba(74,174,136,0.2)', borderRadius: '3px', padding: '10px 14px', color: '#D8E6F0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} value={form.counterBehavior} onChange={e => setForm({ ...form, counterBehavior: e.target.value })} placeholder="Which counter behavior was active?" rows={2} /></div>
        <div style={{ marginBottom: '20px' }}><label style={{ display: 'block', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#4AAE88', marginBottom: '8px' }}>Reflection <span style={{ fontStyle: 'italic', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>optional</span></label><textarea style={{ width: '100%', background: '#0f2236', border: '1px solid rgba(74,174,136,0.2)', borderRadius: '3px', padding: '10px 14px', color: '#D8E6F0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} value={form.reflection} onChange={e => setForm({ ...form, reflection: e.target.value })} placeholder="What shifted? What do you understand now?" rows={3} /></div>
        <div style={{ marginBottom: '28px' }}>
          <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#4AAE88', marginBottom: '8px' }}>Life Period <span style={{ fontStyle: 'italic', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>optional</span></label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {LIFE_PERIODS.map(p => <button key={p.label} style={{ background: form.period === p.label ? 'rgba(74,174,136,0.12)' : 'rgba(74,174,136,0.03)', border: '1px solid ' + (form.period === p.label ? 'rgba(74,174,136,0.5)' : 'rgba(74,174,136,0.15)'), borderRadius: '3px', padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => setForm({ ...form, period: form.period === p.label ? '' : p.label })}><div style={{ fontSize: '11px', fontWeight: '600', color: form.period === p.label ? '#4AAE88' : '#8BAFC8' }}>{p.label}</div><div style={{ fontSize: '10px', color: '#8BAFC8', marginTop: '1px' }}>{p.range}</div></button>)}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <button style={{ background: 'none', border: '1px solid rgba(142,196,224,0.2)', borderRadius: '3px', padding: '10px 20px', color: '#8BAFC8', fontSize: '11px', cursor: 'pointer' }} onClick={onCancel}>Cancel</button>
          <button style={{ background: 'rgba(74,174,136,0.15)', border: '1px solid rgba(74,174,136,0.4)', borderRadius: '3px', padding: '10px 24px', color: '#4AAE88', fontSize: '11px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' }} onClick={() => onSave(form)}>Mark Resolved</button>
        </div>
      </div>
    </div>
  );
}

function GuidedBuilder({ onSave, complexes, prefillBehavior }) {
  const [phase, setPhase] = useState(prefillBehavior ? 'steps' : 'entry');
  const [sequence, setSequence] = useState(prefillBehavior ? SEQUENCES.behaviors : []);
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState(prefillBehavior ? { behaviors: prefillBehavior } : {});

  const selectEntry = (id) => { setSequence(SEQUENCES[id]); setStepIdx(0); setPhase('steps'); };
  const currentKey = sequence[stepIdx];
  const cfg = STEP_CONFIGS[currentKey] || {};
  const isLast = stepIdx === sequence.length - 1;
  const handleNext = () => { if (isLast) onSave({ ...data, status: 'active', originalWound: data.originalWound || false }); else setStepIdx(stepIdx + 1); };
  const handleBack = () => { if (stepIdx === 0) { if (prefillBehavior) return; setPhase('entry'); } else setStepIdx(stepIdx - 1); };
  const availableRoots = complexes.filter(c => c.status !== 'resolved' && c.name !== data.name);

  if (phase === 'entry') {
    return (
      <div>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '26px', fontWeight: '300', color: '#D8E6F0', marginBottom: '32px' }}>Where are you right now?</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {ENTRY_POINTS.map(ep => (
            <button key={ep.id} style={{ background: 'rgba(142,196,224,0.04)', border: '1px solid rgba(142,196,224,0.2)', borderRadius: '3px', padding: '18px 16px', textAlign: 'left', cursor: 'pointer' }}
              onClick={() => selectEntry(ep.id)}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(142,196,224,0.5)'; e.currentTarget.style.background = 'rgba(142,196,224,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(142,196,224,0.2)'; e.currentTarget.style.background = 'rgba(142,196,224,0.04)'; }}>
              <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#8EC4E0', marginBottom: '6px' }}>{ep.label}</div>
              <div style={{ fontSize: '13px', color: '#8BAFC8', lineHeight: 1.5 }}>{ep.desc}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {prefillBehavior && <div style={{ fontSize: '11px', color: '#4AAE88', marginBottom: '20px', padding: '8px 12px', background: 'rgba(74,174,136,0.06)', border: '1px solid rgba(74,174,136,0.2)', borderRadius: '3px' }}>Building from behavior: <strong>{prefillBehavior}</strong></div>}
      <div style={{ fontSize: '9px', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase', color: '#8BAFC8', marginBottom: '20px' }}>{stepIdx + 1} of {sequence.length}</div>
      <div style={{ fontSize: '9px', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase', color: cfg.optional ? '#8BAFC8' : '#8EC4E0', marginBottom: '8px' }}>{cfg.label}{cfg.optional && <span style={{ marginLeft: '8px', fontStyle: 'italic', fontWeight: 400 }}>optional</span>}</div>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: '20px', fontWeight: '300', color: '#D8E6F0', lineHeight: 1.5, marginBottom: '20px' }}>{cfg.q}</div>
      {cfg.type === 'input' && <input style={styles.input} value={data[currentKey] || ''} onChange={e => setData({ ...data, [currentKey]: e.target.value })} placeholder="..." autoFocus onKeyDown={e => { if (e.key === 'Enter') handleNext(); }} />}
      {cfg.type === 'textarea' && <textarea style={{ ...styles.textarea, minHeight: '100px' }} value={data[currentKey] || ''} onChange={e => setData({ ...data, [currentKey]: e.target.value })} placeholder="..." autoFocus rows={4} />}
      {cfg.type === 'select' && <select style={styles.input} value={data[currentKey] || ''} onChange={e => setData({ ...data, [currentKey]: e.target.value })} autoFocus><option value="">-- Select the emotion --</option>{EMOTION_ORDER.map(e => <option key={e} value={e}>{e}</option>)}<option value="unsure">I'm not sure</option></select>}
      {cfg.type === 'period' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {LIFE_PERIODS.map(p => {
            const periods = Array.isArray(data[currentKey]) ? data[currentKey] : (data[currentKey] ? [data[currentKey]] : []);
            const isSelected = periods.includes(p.label);
            return <button key={p.label} style={{ background: isSelected ? 'rgba(142,196,224,0.12)' : 'rgba(142,196,224,0.03)', border: '1px solid ' + (isSelected ? 'rgba(142,196,224,0.6)' : 'rgba(142,196,224,0.15)'), borderRadius: '3px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer' }} onClick={() => { const current = Array.isArray(data[currentKey]) ? data[currentKey] : (data[currentKey] ? [data[currentKey]] : []); const updated = current.includes(p.label) ? current.filter(x => x !== p.label) : [...current, p.label]; const sorted = LIFE_PERIODS.filter(lp => updated.includes(lp.label)).map(lp => lp.label); setData({ ...data, [currentKey]: sorted }); }}><div style={{ fontSize: '12px', fontWeight: '600', color: isSelected ? '#D8E6F0' : '#A0C4D8' }}>{p.label}</div><div style={{ fontSize: '10px', color: '#8BAFC8', marginTop: '2px' }}>{p.range}</div></button>;
          })}
        </div>
      )}
      {cfg.type === 'sourceSelect' && <select style={styles.input} value={data[currentKey] || ''} onChange={e => setData({ ...data, [currentKey]: e.target.value })}><option value="">None / Unknown</option>{SOURCES.map(s => <option key={s} value={s}>{s}</option>)}</select>}
      {cfg.type === 'rootSelect' && <RootComplexSelector value={Array.isArray(data[currentKey]) ? data[currentKey] : []} onChange={(updated) => setData({ ...data, [currentKey]: updated })} availableRoots={availableRoots} />}
      <div style={{ fontSize: '11px', color: 'rgba(142,196,224,0.5)', marginTop: '10px', fontStyle: 'italic' }}>{cfg.hint}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '32px', paddingTop: '20px', borderTop: '1px solid rgba(142,196,224,0.1)' }}>
        <button style={styles.cancelBtn} onClick={handleBack}>{stepIdx === 0 ? (prefillBehavior ? 'Cancel' : 'Entry Point') : 'Back'}</button>
        <button style={styles.btn} onClick={handleNext}>{isLast ? 'Save to Map' : 'Next →'}</button>
      </div>
    </div>
  );
}

function ViewModal({ complex, dreams, people, complexes, onClose, onEdit }) {
  const [viewingDream, setViewingDream] = React.useState(null);
  const [viewingPerson, setViewingPerson] = React.useState(null);
  if (!complex) return null;
  const c = complex;
  const hasCounter = c.counter && c.counter.trim();
  const hasCounterBehavior = c.counterBehavior && c.counterBehavior.trim();
  const isInnerChild = Array.isArray(c.period) ? (c.period.includes('Early Childhood') || c.period.includes('Childhood')) : (c.period === 'Early Childhood' || c.period === 'Childhood');
  const W = 220; const CW = 180; const GAP = 16;

  const Arrow = ({ up, color }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '28px' }}>
      {up && <div style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: '7px solid ' + (color || 'rgba(142,196,224,0.35)') }} />}
      <div style={{ width: '2px', flex: 1, background: color || 'rgba(142,196,224,0.35)' }} />
      {!up && <div style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '7px solid ' + (color || 'rgba(142,196,224,0.35)') }} />}
    </div>
  );

  const FlowNode = ({ label, text, isBurden, isTrigger, isCounter }) => (
    <div style={{ border: '1px solid ' + (isCounter ? 'rgba(74,174,136,0.3)' : isBurden ? 'rgba(176,90,90,0.35)' : isTrigger ? 'rgba(200,168,80,0.3)' : 'rgba(142,196,224,0.2)'), borderRadius: '3px', padding: '12px 14px', background: isCounter ? 'rgba(74,174,136,0.06)' : isBurden ? 'rgba(176,90,90,0.08)' : isTrigger ? 'rgba(200,168,80,0.06)' : 'rgba(142,196,224,0.04)', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ fontSize: '8px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', color: isCounter ? '#4AAE88' : isBurden ? '#C87878' : isTrigger ? '#C8A840' : '#8EC4E0', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: '#D8E6F0', fontFamily: 'Georgia, serif', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{text || ''}</div>
    </div>
  );

  const Row = ({ main, counter, connector }) => (
    <div style={{ display: 'flex', alignItems: 'stretch', width: W + GAP + CW }}>
      <div style={{ width: W, flexShrink: 0 }}>{main}</div>
      <div style={{ width: GAP, flexShrink: 0, display: 'flex', alignItems: 'center' }}>{connector}</div>
      <div style={{ width: CW, flexShrink: 0 }}>{counter}</div>
    </div>
  );

  const ArrowRow = ({ left, right }) => (
    <div style={{ display: 'flex', width: W + GAP + CW }}>
      <div style={{ width: W, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>{left}</div>
      <div style={{ width: GAP }} />
      <div style={{ width: CW, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>{right}</div>
    </div>
  );

  const Connector = () => <div style={{ height: '2px', width: '100%', background: 'rgba(74,174,136,0.4)' }} />;
  const bVal = Array.isArray(c.behaviors) ? c.behaviors.join('\n') : (c.behaviors || '');
  const InnerChildNote = () => <div style={{ fontSize: '9px', fontStyle: 'italic', color: 'rgba(200,168,80,0.6)', marginTop: '6px' }}>You are speaking to your inner child.</div>;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 20px' }}>
      {viewingPerson && (() => {
        const personComplexes = (complexes || []).filter(cx => cx.person === viewingPerson.name);
        const personDreams = (dreams || []).filter(d => {
          const ppl = Array.isArray(d.people) ? d.people : [];
          return ppl.includes(viewingPerson.name);
        });
        const sortedPersonDreams = personDreams.slice().sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        const cur = viewingPerson.currentPartner || viewingPerson.partner || null;
        const past = Array.isArray(viewingPerson.pastPartners) ? viewingPerson.pastPartners : [];
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 20px' }}>
            <div style={{ background: '#162534', border: '1px solid rgba(142,196,224,0.3)', borderRadius: '4px', width: '100%', maxWidth: '520px', padding: '32px', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', fontWeight: '300', color: '#D8E6F0' }}>
                    {viewingPerson.name}
                    {viewingPerson.isSelf && <span style={{ color: '#C8A840', marginLeft: '8px', fontSize: '14px' }}>◉</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
                    {viewingPerson.level && <span style={{ fontSize: '10px', color: '#8BAFC8', letterSpacing: '1px' }}>Level {viewingPerson.level}</span>}
                    {viewingPerson.familyOrigin && viewingPerson.familyOrigin !== 'none' && <span style={{ fontSize: '10px', color: '#8BAFC8', letterSpacing: '1px', textTransform: 'uppercase' }}>· {viewingPerson.familyOrigin}</span>}
                    {cur && <span style={{ fontSize: '10px', color: '#C8A840', letterSpacing: '1px' }}>· ↔ {cur}</span>}
                    {past.length > 0 && <span style={{ fontSize: '10px', color: '#8BAFC8', letterSpacing: '1px', fontStyle: 'italic' }}>· past: {past.join(', ')}</span>}
                  </div>
                  {viewingPerson.pattern && <div style={{ fontSize: '13px', color: '#A0C4D8', fontStyle: 'italic', marginTop: '10px' }}>{viewingPerson.pattern}</div>}
                </div>
                <button style={{ background: 'none', border: 'none', color: '#8BAFC8', cursor: 'pointer', fontSize: '18px' }} onClick={() => setViewingPerson(null)}>✕</button>
              </div>
              {personComplexes.length > 0 && (
                <CollapsibleSection
                  label={`Complexes (${personComplexes.length})`}
                  color="#8BAFC8"
                  defaultOpen={true}
                >
                  {personComplexes.map((cx, i) => (
                    <div key={i} style={{ border: '1px solid rgba(74,174,136,0.2)', borderRadius: '3px', padding: '10px 14px', background: 'rgba(74,174,136,0.04)', marginBottom: '6px' }}>
                      <div style={{ fontSize: '13px', color: '#D8E6F0', fontFamily: 'Georgia, serif' }}>{cx.name}</div>
                      {cx.burden && <div style={{ fontSize: '9px', color: '#8BAFC8', letterSpacing: '1px', marginTop: '2px', textTransform: 'uppercase' }}>{cx.burden}</div>}
                    </div>
                  ))}
                </CollapsibleSection>
              )}
              {sortedPersonDreams.length > 0 && (
                <CollapsibleSection
                  label={`Dreams (${sortedPersonDreams.length})`}
                  color="#B07ED4"
                  defaultOpen={false}
                >
                  {sortedPersonDreams.map((d, i) => (
                    <div key={i} onClick={() => { setViewingPerson(null); setViewingDream(d); }} style={{ border: '1px solid rgba(176,126,212,0.2)', borderRadius: '3px', padding: '10px 14px', background: 'rgba(176,126,212,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: '6px' }}>
                      <div>
                        <div style={{ fontSize: '13px', color: '#D8E6F0', fontFamily: 'Georgia, serif' }}>{d.title || 'Untitled Dream'}</div>
                        {d.year && <div style={{ fontSize: '9px', color: '#8BAFC8', letterSpacing: '1px', marginTop: '2px' }}>{d.year}</div>}
                      </div>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4 2 L9 6 L4 10" stroke="rgba(176,126,212,0.5)" strokeWidth="1.5" strokeLinecap="round" /></svg>
                    </div>
                  ))}
                </CollapsibleSection>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(142,196,224,0.15)' }}>
                <button style={{ background: 'rgba(142,196,224,0.15)', border: '1px solid rgba(142,196,224,0.4)', borderRadius: '3px', padding: '10px 20px', color: '#8EC4E0', fontSize: '11px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' }} onClick={() => setViewingPerson(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}
      {viewingDream && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 20px' }}>
          <div style={{ background: '#162534', border: '1px solid rgba(176,126,212,0.3)', borderRadius: '4px', width: '100%', maxWidth: '520px', padding: '32px', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', fontWeight: '300', color: '#D8E6F0' }}>{viewingDream.title || 'Untitled Dream'}</div>
              <button style={{ background: 'none', border: 'none', color: '#8BAFC8', cursor: 'pointer', fontSize: '18px' }} onClick={() => setViewingDream(null)}>✕</button>
            </div>
            {viewingDream.date && <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase', color: '#8BAFC8', marginBottom: '20px' }}>{new Date(viewingDream.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>}
            {[
              { key: 'narrative', label: 'Narrative' },
              { key: 'people', label: 'Who Appeared' },
              { key: 'symbols', label: 'Symbols & Recurring Themes' },
              { key: 'reflection', label: 'Reflection' },
            ].map(field => {
              const val = viewingDream[field.key];
              if (!val) return null;
              const display = Array.isArray(val) ? val.join(', ') : val;
              if (!display || (typeof display === 'string' && !display.trim())) return null;
              return (
                <div key={field.key} style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '9px', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase', color: '#8BAFC8', marginBottom: '8px' }}>{field.label}</div>
                  <div style={{ fontSize: '13px', color: '#D8E6F0', fontFamily: 'Georgia, serif', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{display}</div>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button style={{ background: 'rgba(176,126,212,0.1)', border: '1px solid rgba(176,126,212,0.3)', borderRadius: '3px', padding: '10px 24px', color: '#B07ED4', fontSize: '11px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' }} onClick={() => setViewingDream(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ background: '#162534', border: '1px solid rgba(142,196,224,0.3)', borderRadius: '4px', width: '100%', maxWidth: '560px', padding: '32px', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', fontWeight: '300', color: '#D8E6F0' }}>{c.name}</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
              {c.originalWound && <span style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#C8A840', border: '1px dashed #C8A840', padding: '2px 7px', borderRadius: '2px' }}>Original Wound</span>}
              {c.status === 'resolved' && <span style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#4AAE88', border: '1px solid rgba(74,174,136,0.4)', padding: '2px 7px', borderRadius: '2px' }}>Resolved</span>}
              {c.period && <span style={{ fontSize: '10px', color: '#8BAFC8', letterSpacing: '1px' }}>{getPeriodDisplay(c.period)}</span>}
              {c.source && <span style={{ fontSize: '10px', color: '#8BAFC8', letterSpacing: '1px' }}>· {c.source}</span>}
            </div>
          </div>
          <button style={{ background: 'none', border: 'none', color: '#8BAFC8', cursor: 'pointer', fontSize: '18px' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ overflowX: 'auto', paddingBottom: '8px', marginTop: '16px' }}>
          <Row main={<FlowNode label="Underlying Emotional Burden" text={c.burden || ''} isBurden />} counter={<div />} connector={<div />} />
          <ArrowRow left={<Arrow />} right={<div />} />
          <Row main={<FlowNode label="Beliefs" text={c.beliefs || ''} />} connector={hasCounter ? <Connector /> : <div />} counter={hasCounter ? <><FlowNode label="Counter Beliefs" text={c.counter} isCounter />{isInnerChild && <InnerChildNote />}</> : <div />} />
          <ArrowRow left={<Arrow />} right={<div />} />
          <Row main={<FlowNode label="Thoughts" text={c.thoughts || ''} />} connector={<div />} counter={<div />} />
          {c.feelings && c.feelings.trim() && <><ArrowRow left={<Arrow />} right={<div />} /><Row main={<FlowNode label="Feelings" text={c.feelings} />} connector={<div />} counter={<div />} /></>}
          <ArrowRow left={<Arrow />} right={hasCounterBehavior ? <Arrow color="rgba(74,174,136,0.4)" /> : <div />} />
          <Row main={<FlowNode label="Behaviors" text={bVal} />} connector={hasCounterBehavior ? <Connector /> : <div />} counter={hasCounterBehavior ? <><FlowNode label="Counter Behaviors" text={c.counterBehavior} isCounter />{isInnerChild && <InnerChildNote />}</> : <div />} />
          <ArrowRow left={<Arrow up />} right={<div />} />
          <Row main={<FlowNode label="Triggers" text={c.trigger || ''} isTrigger />} connector={<div />} counter={<div />} />
          {c.notes && c.notes.trim() && <div style={{ marginTop: '20px', opacity: 0.7, width: W + GAP + CW }}><FlowNode label="Notes" text={c.notes} /></div>}
        </div>
        {c.resolution && c.resolution.what && (
          <div style={{ marginTop: '20px', padding: '16px', border: '1px solid rgba(74,174,136,0.25)', borderRadius: '3px', background: 'rgba(74,174,136,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <ConcentricSquare size={20} color="rgba(74,174,136,0.7)" />
              <div style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', color: '#4AAE88' }}>Resolution</div>
            </div>
            <div style={{ fontSize: '13px', color: '#A0C4D8', fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>{c.resolution.what}</div>
          </div>
        )}
        {c.rootComplex && (Array.isArray(c.rootComplex) ? c.rootComplex.length > 0 : c.rootComplex) && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(142,196,224,0.1)' }}>
            <div style={{ fontSize: '9px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#8BAFC8', marginBottom: '6px' }}>Root Complex</div>
            <div style={{ fontSize: '13px', color: '#A0C4D8' }}>{Array.isArray(c.rootComplex) ? c.rootComplex.join(', ') : c.rootComplex}</div>
          </div>
        )}
        {c.person && people && (() => {
          const personObj = people.find(p => p.name === c.person);
          if (!personObj) return null;
          return (
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(142,196,224,0.1)' }}>
              <div style={{ fontSize: '9px', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase', color: '#8EC4E0', marginBottom: '8px' }}>Linked Person</div>
              <div
                onClick={() => setViewingPerson(personObj)}
                style={{ border: '1px solid rgba(142,196,224,0.25)', borderRadius: '3px', padding: '12px 16px', background: 'rgba(142,196,224,0.05)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <div>
                  <div style={{ fontSize: '14px', color: '#D8E6F0', fontFamily: 'Georgia, serif', marginBottom: '4px' }}>
                    {personObj.name}
                    {personObj.isSelf && <span style={{ color: '#C8A840', marginLeft: '6px', fontSize: '11px' }}>◉</span>}
                  </div>
                  {personObj.pattern && <div style={{ fontSize: '11px', color: '#A0C4D8', fontStyle: 'italic' }}>{personObj.pattern}</div>}
                </div>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4 2 L9 6 L4 10" stroke="rgba(142,196,224,0.5)" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </div>
            </div>
          );
        })()}
        {dreams && (() => {
          const linkedDreams = dreams.filter(d => {
            const links = Array.isArray(d.complexLinks) ? d.complexLinks : (d.complexLink ? [d.complexLink] : []);
            return links.includes(c.name);
          });
          if (linkedDreams.length === 0) return null;
          const sorted = linkedDreams.slice().sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
          return (
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(142,196,224,0.1)' }}>
              <div style={{ fontSize: '9px', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase', color: '#B07ED4', marginBottom: '8px' }}>Linked Dream{sorted.length > 1 ? 's' : ''} ({sorted.length})</div>
              {sorted.map((linkedDream, i) => (
                <div key={i} style={{ border: '1px solid rgba(176,126,212,0.2)', borderRadius: '3px', padding: '12px 16px', background: 'rgba(176,126,212,0.04)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: i < sorted.length - 1 ? '8px' : 0 }} onClick={() => setViewingDream(linkedDream)}>
                  <div>
                    <div style={{ fontSize: '14px', color: '#D8E6F0', fontFamily: 'Georgia, serif', marginBottom: '4px' }}>{linkedDream.title || 'Untitled Dream'}</div>
                    <div style={{ fontSize: '10px', color: '#8BAFC8', letterSpacing: '1px' }}>{linkedDream.date ? new Date(linkedDream.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
                  </div>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4 2 L9 6 L4 10" stroke="rgba(176,126,212,0.5)" strokeWidth="1.5" strokeLinecap="round" /></svg>
                </div>
              ))}
            </div>
          );
        })()}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(142,196,224,0.15)' }}>
          <button style={styles.cancelBtn} onClick={onClose}>Close</button>
          <button style={styles.btn} onClick={onEdit}>Edit</button>
        </div>
      </div>
    </div>
  );
}

function TreeView({ complexes, people, patterns, activeCategoryId, maximized, onToggleMax, onViewComplex, onViewResolution, onSaveOrder, savedOrder }) {
  const viewportRef = useRef(null);
  const treeRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [panX, setPanX] = useState(20);
  const [panY, setPanY] = useState(20);
  const [groupOrder, setGroupOrder] = useState(savedOrder || {});
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const panStart = useRef({ x: 20, y: 20 });

  const NODE_W = 200; const NODE_H = 72; const H_GAP = 40; const V_GAP = 80; const GROUP_GAP = 60;
  const LABEL_H = 28;
  const RES_W = 64; const RES_H = 64;

  const nameToIdx = {};
  complexes.forEach((c, i) => { nameToIdx[c.name] = i; });
  const allNodePositions = useRef({});

  const subtreeWidth = (node) => {
    const extras = node.showResolution ? [{ isResolution: true }] : [];
    const all = [...node.children, ...extras];
    if (all.length === 0) return NODE_W;
    let total = 0;
    all.forEach((child, i) => { total += child.isResolution ? RES_W : subtreeWidth(child); if (i < all.length - 1) total += H_GAP; });
    return Math.max(NODE_W, total);
  };

  const assignPositions = (node, x, y, groupOffsetX = 0) => {
    const sw = subtreeWidth(node);
    node.x = x + sw / 2 - NODE_W / 2;
    node.y = y;
    allNodePositions.current[node.name] = { x: node.x + groupOffsetX, y: node.y };
    const extras = node.showResolution ? [{ isResolution: true }] : [];
    const all = [...node.children, ...extras];
    if (all.length > 0) {
      let cx = x;
      all.forEach(child => {
        if (child.isResolution) { child.x = cx; child.y = y + NODE_H + V_GAP; cx += RES_W + H_GAP; }
        else { const csw = subtreeWidth(child); assignPositions(child, cx, y + NODE_H + V_GAP, groupOffsetX); cx += csw + H_GAP; }
      });
    }
  };

  const buildNode = (name, visited = {}) => {
    if (visited[name]) return null;
    visited[name] = true;
    const c = complexes[nameToIdx[name]];
    const isResolved = c && c.status === 'resolved';
    const children = [];
    complexes.forEach(child => {
      const roots = Array.isArray(child.rootComplex) ? child.rootComplex : (child.rootComplex ? [child.rootComplex] : []);
      if (roots[0] === name && !visited[child.name]) { const childNode = buildNode(child.name, visited); if (childNode) children.push(childNode); }
    });
    children.sort((a, b) => { const ca = complexes[nameToIdx[a.name]]; const cb = complexes[nameToIdx[b.name]]; return getPeriodOrder(ca && ca.period) - getPeriodOrder(cb && cb.period); });
    return { name, children, showResolution: isResolved && children.length === 0 };
  };

  const getUltimateRoot = (c) => {
    let cur = c; const visited = {};
    while (cur) {
      const curRoots = Array.isArray(cur.rootComplex) ? cur.rootComplex : (cur.rootComplex ? [cur.rootComplex] : []);
      if (curRoots.length === 0) break;
      if (visited[cur.name]) break;
      visited[cur.name] = true;
      const parent = complexes[nameToIdx[curRoots[0]]];
      if (parent) { cur = parent; } else break;
    }
    return cur;
  };

  const rootSet = {}; const rootNames = [];
  complexes.forEach(c => { const root = getUltimateRoot(c); if (!rootSet[root.name]) { rootSet[root.name] = true; rootNames.push(root.name); } });
  rootNames.sort((a, b) => getPeriodOrder(complexes[nameToIdx[a]] && complexes[nameToIdx[a]].period) - getPeriodOrder(complexes[nameToIdx[b]] && complexes[nameToIdx[b]].period));

  const sourceGroups = {};
  rootNames.forEach(name => { const c = complexes[nameToIdx[name]]; const src = (c && c.source) || ''; if (!sourceGroups[src]) sourceGroups[src] = []; sourceGroups[src].push(name); });
  const namedSources = Object.keys(sourceGroups).filter(s => s !== '');
  namedSources.sort((a, b) => {
    const aIsPredef = SOURCE_ORDER[a] !== undefined && a !== 'Custom';
    const bIsPredef = SOURCE_ORDER[b] !== undefined && b !== 'Custom';
    if (aIsPredef && bIsPredef) return SOURCE_ORDER[a] - SOURCE_ORDER[b];
    if (aIsPredef && !bIsPredef) return -1;
    if (!aIsPredef && bIsPredef) return 1;
    return a.localeCompare(b);
  });
  const orderedSources = [...namedSources, ...(sourceGroups[''] && sourceGroups[''].length > 0 ? [''] : [])];

  const getOrderedGroupRoots = (source) => {
    const defaults = sourceGroups[source] || [];
    const saved = groupOrder[source];
    if (!saved) return defaults;
    const existing = saved.filter(n => defaults.includes(n));
    const newOnes = defaults.filter(n => !saved.includes(n));
    return [...existing, ...newOnes];
  };

  const renderedGroups = []; let globalX = 0;
  allNodePositions.current = {};
  const groupSlotCenters = {};

  orderedSources.forEach(source => {
    const groupRoots = getOrderedGroupRoots(source);
    const groupTrees = []; let localX = 0;
    const slotCenters = [];
    groupRoots.forEach((rootName, ri) => {
      const tree = buildNode(rootName, {}); if (!tree) return;
      const treeW = subtreeWidth(tree);
      assignPositions(tree, localX, 0, globalX);
      const allNodes = []; const flatCollect = (node, arr) => { arr.push(node); node.children.forEach(c => flatCollect(c, arr)); };
      flatCollect(tree, allNodes);
      slotCenters.push({ name: rootName, centerX: globalX + localX + treeW / 2, slotX: localX, treeW });
      localX += treeW + (ri < groupRoots.length - 1 ? H_GAP : 0);
      groupTrees.push({ tree, allNodes });
    });
    groupSlotCenters[source] = slotCenters;
    renderedGroups.push({ source, groupTrees, groupWidth: localX, offsetX: globalX });
    globalX += localX + GROUP_GAP;
  });

  const totalW = globalX;
  let totalH = 0;
  renderedGroups.forEach(g => g.groupTrees.forEach(t => { t.allNodes.forEach(n => { if (n.y + NODE_H > totalH) totalH = n.y + NODE_H; }); }));
  totalH += V_GAP + RES_H;

  useEffect(() => {
    const viewport = viewportRef.current; if (!viewport) return;
    const onMouseDown = (e) => {
      if (e.target.closest('[data-root-handle]')) return;
      isDragging.current = true; dragStart.current = { x: e.clientX, y: e.clientY }; panStart.current = { x: panX, y: panY }; viewport.style.cursor = 'grabbing'; e.preventDefault();
    };
    const onMouseMove = (e) => { if (!isDragging.current) return; setPanX(panStart.current.x + (e.clientX - dragStart.current.x)); setPanY(panStart.current.y + (e.clientY - dragStart.current.y)); };
    const onMouseUp = () => { isDragging.current = false; viewport.style.cursor = 'grab'; };
    const onWheel = (e) => {
      e.preventDefault();
      const rect = viewport.getBoundingClientRect(); const mouseX = e.clientX - rect.left; const mouseY = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale(prev => { const newScale = Math.min(3, Math.max(0.2, prev + delta)); setPanX(px => mouseX - (mouseX - px) * (newScale / prev)); setPanY(py => mouseY - (mouseY - py) * (newScale / prev)); return newScale; });
    };
    viewport.addEventListener('mousedown', onMouseDown); window.addEventListener('mousemove', onMouseMove); window.addEventListener('mouseup', onMouseUp); viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => { viewport.removeEventListener('mousedown', onMouseDown); window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); viewport.removeEventListener('wheel', onWheel); };
  }, [panX, panY]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRootDrag = (e, source, rootName) => {
    e.stopPropagation(); e.preventDefault();
    const startMouseX = e.clientX;
    const slots = groupSlotCenters[source] || [];
    let currentOrder = getOrderedGroupRoots(source).slice();
    const onMove = (ev) => {
      const dx = (ev.clientX - startMouseX) / scale;
      const pos = allNodePositions.current[rootName];
      const movedCenterX = (pos ? pos.x : 0) + dx + NODE_W / 2;
      let closestSlot = currentOrder.indexOf(rootName); let closestDist = Infinity;
      slots.forEach((s, i) => { const d = Math.abs(s.centerX - movedCenterX); if (d < closestDist) { closestDist = d; closestSlot = i; } });
      const newOrder = currentOrder.filter(n => n !== rootName); newOrder.splice(closestSlot, 0, rootName);
      if (JSON.stringify(newOrder) !== JSON.stringify(currentOrder)) { currentOrder = newOrder; setGroupOrder(prev => ({ ...prev, [source]: newOrder })); }
    };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); setGroupOrder(prev => { onSaveOrder({ ...prev }); return prev; }); };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  };

  if (complexes.length === 0) return <div style={{ textAlign: 'center', padding: '48px', fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#8BAFC8' }}>No complexes yet. Build your first complex to begin mapping.</div>;

  const getResolutionPos = (node) => ({ x: node.x + NODE_W / 2 - RES_W / 2, y: node.y + NODE_H + V_GAP });

  const buildAllLines = () => {
    const lines = [];
    const traverse = (node) => {
      const nodeGlobalX = allNodePositions.current[node.name] ? allNodePositions.current[node.name].x : 0;
      node.children.forEach(child => {
        const childGlobalX = allNodePositions.current[child.name] ? allNodePositions.current[child.name].x : 0;
        const px = nodeGlobalX + NODE_W / 2; const py = LABEL_H + node.y + NODE_H;
        const cx = childGlobalX + NODE_W / 2; const cy = LABEL_H + child.y;
        const my = py + (cy - py) / 2;
        lines.push({ d: 'M' + px + ',' + py + ' L' + px + ',' + my + ' L' + cx + ',' + my + ' L' + cx + ',' + cy, dashed: false });
        const childComplex = complexes[nameToIdx[child.name]];
        if (childComplex) {
          const childRoots = Array.isArray(childComplex.rootComplex) ? childComplex.rootComplex : [];
          childRoots.slice(1).forEach(secRootName => {
            const secPos = allNodePositions.current[secRootName];
            if (secPos) {
              const spx = secPos.x + NODE_W / 2; const spy = LABEL_H + secPos.y + NODE_H; const smy = spy + (cy - spy) / 2;
              lines.push({ d: 'M' + spx + ',' + spy + ' L' + spx + ',' + smy + ' L' + cx + ',' + smy + ' L' + cx + ',' + cy, dashed: true });
            }
          });
        }
        traverse(child);
      });
      if (node.showResolution) {
        const resGlobalX = nodeGlobalX + NODE_W / 2;
        const py = LABEL_H + node.y + NODE_H; const cy = LABEL_H + node.y + NODE_H + V_GAP;
        lines.push({ d: 'M' + resGlobalX + ',' + py + ' L' + resGlobalX + ',' + cy, dashed: true, isResolution: true });
      }
    };
    renderedGroups.forEach(group => group.groupTrees.forEach(({ tree }) => traverse(tree)));
    return lines;
  };

  const allLines = buildAllLines();

  const renderNodes = (node, gi, ti, source) => {
    const items = [];
    const c = complexes[nameToIdx[node.name]];
    const colors = (c && TREE_COLORS[c.burden]) || DEFAULT_TREE_COLORS;
    const isWound = c && c.originalWound === true;
    const resolved = c && c.status === 'resolved';
    const isRoot = !c || !c.rootComplex || (Array.isArray(c.rootComplex) ? c.rootComplex.length === 0 : !c.rootComplex);

    let patternBorder = null;
    let patternBg = null;
    if (activeCategoryId && c && c.person && people && patterns) {
      const personObj = people.find(p => p.name === c.person);
      if (personObj && personObj.patterns && personObj.patterns[activeCategoryId]) {
        const pat = patterns.find(pp => pp.id === personObj.patterns[activeCategoryId]);
        if (pat) {
          patternBorder = pat.color;
          patternBg = pat.color + '15';
        }
      }
    }

    let finalBorder, finalLeftBorder, finalBg, finalDot, finalNameColor, finalBurdenColor, finalPeriodColor;
    if (activeCategoryId) {
      if (patternBorder) {
        finalBorder = '2px solid ' + patternBorder;
        finalLeftBorder = '3px solid ' + patternBorder;
        finalBg = patternBg;
        finalDot = patternBorder;
        finalNameColor = '#D8E6F0';
        finalBurdenColor = patternBorder;
        finalPeriodColor = 'rgba(142,196,224,0.55)';
      } else {
        finalBorder = '1px solid rgba(142,196,224,0.15)';
        finalLeftBorder = '3px solid rgba(142,196,224,0.15)';
        finalBg = 'rgba(142,196,224,0.02)';
        finalDot = 'rgba(142,196,224,0.3)';
        finalNameColor = 'rgba(216,230,240,0.4)';
        finalBurdenColor = 'rgba(142,196,224,0.3)';
        finalPeriodColor = 'rgba(142,196,224,0.3)';
      }
    } else {
      finalBorder = '1px solid ' + (resolved ? 'rgba(74,174,136,0.5)' : colors.border);
      finalLeftBorder = '3px solid ' + (resolved ? 'rgba(74,174,136,0.6)' : colors.border);
      finalBg = resolved ? 'rgba(74,174,136,0.06)' : colors.bg;
      finalDot = resolved ? '#4AAE88' : colors.dot;
      finalNameColor = resolved ? '#4AAE88' : '#D8E6F0';
      finalBurdenColor = resolved ? 'rgba(74,174,136,0.6)' : colors.dot;
      finalPeriodColor = resolved ? 'rgba(74,174,136,0.5)' : 'rgba(142,196,224,0.55)';
    }

    items.push(
      <div key={'n-' + gi + '-' + ti + '-' + node.name}
        data-root-handle={isRoot ? 'true' : undefined}
        onMouseDown={isRoot ? (e) => startRootDrag(e, source, node.name) : undefined}
        onClick={(e) => { if (!e.defaultPrevented && c) onViewComplex(nameToIdx[node.name]); }}
        style={{ position: 'absolute', left: node.x, top: LABEL_H + node.y, width: NODE_W, height: NODE_H, border: finalBorder, borderLeft: finalLeftBorder, background: finalBg, borderRadius: '3px', padding: '8px 12px', cursor: isRoot ? 'grab' : 'pointer', boxSizing: 'border-box', overflow: 'hidden', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
          <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: finalDot, flexShrink: 0 }} />
          <span style={{ fontSize: '12px', fontWeight: '600', color: finalNameColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.name}</span>
          {isWound && <span style={{ color: '#C8A840', fontSize: '13px', flexShrink: 0 }}>◉</span>}
          {resolved && <span style={{ fontSize: '10px', color: 'rgba(74,174,136,0.6)', flexShrink: 0 }}>✓</span>}
          {isRoot && <span style={{ fontSize: '9px', color: 'rgba(142,196,224,0.4)', marginLeft: 'auto', flexShrink: 0 }}>⇄</span>}
        </div>
        {c && c.burden && <div style={{ fontSize: '8px', letterSpacing: '2px', textTransform: 'uppercase', color: finalBurdenColor, marginLeft: '13px' }}>{c.burden}</div>}
        {c && c.period && <div style={{ fontSize: '8px', color: finalPeriodColor, marginLeft: '13px', marginTop: '2px', letterSpacing: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getPeriodDisplay(c.period)}</div>}
      </div>
    );

    if (node.showResolution) {
      const rPos = getResolutionPos(node);
      const resolvedComplexesForNode = [];
      let cur = complexes[nameToIdx[node.name]];
      while (cur) {
        if (cur.status === 'resolved') resolvedComplexesForNode.unshift(cur);
        const roots = Array.isArray(cur.rootComplex) ? cur.rootComplex : (cur.rootComplex ? [cur.rootComplex] : []);
        if (roots.length === 0) break;
        cur = complexes[nameToIdx[roots[0]]];
      }
      items.push(
        <div key={'res-' + gi + '-' + ti + '-' + node.name} onClick={() => onViewResolution(resolvedComplexesForNode.filter(Boolean))}
          style={{ position: 'absolute', left: rPos.x, top: LABEL_H + rPos.y, width: RES_W, height: RES_H, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'rgba(74,174,136,0.04)', border: '1px solid rgba(74,174,136,0.2)', borderRadius: '4px' }}>
          <ConcentricSquare size={44} color="rgba(74,174,136,0.7)" />
        </div>
      );
    }

    node.children.forEach(child => renderNodes(child, gi, ti, source).forEach(el => items.push(el)));
    return items;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '0 32px' }}>
        <button style={styles.treeCtrlBtn} onClick={() => setScale(s => Math.min(3, s + 0.2))}>+</button>
        <button style={styles.treeCtrlBtn} onClick={() => setScale(s => Math.max(0.2, s - 0.2))}>−</button>
        <button style={{ ...styles.treeCtrlBtn, fontSize: '9px', letterSpacing: '2px', padding: '0 12px' }} onClick={() => { setScale(1); setPanX(20); setPanY(20); }}>Fit</button>
        {onToggleMax && <button style={{ ...styles.treeCtrlBtn, width: 'auto', fontSize: '9px', letterSpacing: '2px', padding: '0 12px' }} onClick={onToggleMax}>{maximized ? '⤡ Collapse' : '⤢ Expand'}</button>}
        <span style={{ fontSize: '10px', color: '#8BAFC8', letterSpacing: '2px' }}>{Math.round(scale * 100)}%</span>
        <span style={{ fontSize: '9px', color: 'rgba(142,196,224,0.45)', marginLeft: '8px', letterSpacing: '1px' }}>Drag ⇄ root nodes to reorder</span>
      </div>
      <div ref={viewportRef} style={{ flex: 1, overflow: 'hidden', background: 'rgba(142,196,224,0.02)', cursor: 'grab', position: 'relative', borderTop: '1px solid rgba(142,196,224,0.1)' }}>
        <div ref={treeRef} data-export-target="cpm-tree" data-export-width={totalW} data-export-height={totalH + LABEL_H + 20} style={{ position: 'absolute', transformOrigin: '0 0', transform: 'translate(' + panX + 'px,' + panY + 'px) scale(' + scale + ')', willChange: 'transform', width: totalW, height: totalH + LABEL_H + 20 }}>
          <svg style={{ position: 'absolute', top: 0, left: 0, width: totalW, height: totalH + LABEL_H + 20, overflow: 'visible', pointerEvents: 'none' }}>
            {allLines.map((l, i) => (
              <path key={i} d={l.d} fill="none"
                stroke={l.isResolution ? 'rgba(74,174,136,0.4)' : l.dashed ? 'rgba(142,196,224,0.3)' : 'rgba(142,196,224,0.4)'}
                strokeWidth="1.5"
                strokeDasharray={l.dashed ? '4,3' : 'none'} />
            ))}
          </svg>
          {renderedGroups.map((group, gi) => (
            <div key={gi} style={{ position: 'absolute', left: group.offsetX, top: 0, width: group.groupWidth, height: totalH + LABEL_H + 20 }}>
              {group.source && <div style={{ position: 'absolute', top: 0, left: 0, fontSize: '9px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', color: 'rgba(142,196,224,0.55)', borderBottom: '1px solid rgba(142,196,224,0.15)', paddingBottom: '6px', width: '100%' }}>{group.source}</div>}
              {group.groupTrees.map(({ tree }, ti) => renderNodes(tree, gi, ti, group.source))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HowItWorksModal({ onClose }) {
  const head = { fontFamily: 'Georgia, serif', fontSize: '17px', fontWeight: '400', color: '#8EC4E0', margin: '26px 0 10px', letterSpacing: '0.5px' };
  const para = { fontSize: '13px', lineHeight: 1.75, color: '#B3C9DA', margin: '0 0 14px' };

  const esmRows = [
    ['Survival', 'Secure', 'Fear', 'The right to feel safe'],
    ['Action', 'Free', 'Guilt', 'The right to autonomous expression'],
    ['Identity', 'Empowered', 'Shame', 'The right to be'],
    ['Boundary', 'At Peace', 'Anger', 'The right to be respected'],
    ['Comparison', 'Abundant', 'Envy', 'The right to be seen'],
    ['Love', 'Connected', 'Grief', 'The right to love and be loved'],
  ];
  const esmGrid = { display: 'grid', gridTemplateColumns: '92px 1fr 1fr 1.6fr', gap: '12px', alignItems: 'center' };

  const SpineRow = ({ label, color, border, bg, counter, note }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={{ width: '150px', flexShrink: 0, border: '1px solid ' + border, background: bg, borderRadius: '3px', padding: '9px 12px', textAlign: 'center', boxSizing: 'border-box' }}>
        <span style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: color }}>{label}</span>
      </div>
      {counter && <div style={{ width: '28px', height: '1px', background: 'rgba(74,174,136,0.4)', flexShrink: 0 }} />}
      {counter && (
        <div style={{ width: '150px', flexShrink: 0, border: '1px solid rgba(74,174,136,0.35)', background: 'rgba(74,174,136,0.06)', borderRadius: '3px', padding: '9px 12px', textAlign: 'center', boxSizing: 'border-box' }}>
          <span style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#4AAE88' }}>{counter}</span>
        </div>
      )}
      {note && <span style={{ marginLeft: '12px', fontSize: '10px', fontStyle: 'italic', color: '#8BAFC8', whiteSpace: 'nowrap' }}>{note}</span>}
    </div>
  );

  const Down = () => <div style={{ width: '150px', textAlign: 'center', color: 'rgba(142,196,224,0.45)', fontSize: '10px', margin: '5px 0' }}>▼</div>;
  const Up = ({ note }) => (
    <div style={{ display: 'flex', alignItems: 'center', margin: '5px 0' }}>
      <div style={{ width: '150px', textAlign: 'center', color: 'rgba(200,168,80,0.6)', fontSize: '10px' }}>▲</div>
      {note && <span style={{ marginLeft: '12px', fontSize: '10px', fontStyle: 'italic', color: '#8BAFC8' }}>{note}</span>}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 20px' }} onClick={onClose}>
      <div style={{ background: '#162534', border: '1px solid rgba(142,196,224,0.3)', borderRadius: '4px', width: '100%', maxWidth: '720px', padding: '32px', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }} onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '4px' }}>
          <div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', fontWeight: '300', color: '#D8E6F0' }}>How It Works</div>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase', color: '#8BAFC8', marginTop: '4px' }}>Complex Pattern Map</div>
          </div>
          <button style={{ background: 'none', border: 'none', color: '#8BAFC8', cursor: 'pointer', fontSize: '18px' }} onClick={onClose}>✕</button>
        </div>

        <p style={{ ...para, marginTop: '18px' }}>
          The emotional burden is at the root of the complex. Each emotional burden can hold multiple complexes; they can be seen as psychic knots. To untie the knot, to dissolve the burden, the tool is awareness. Once the complexes are dissolved, the emotional burden lifts, and you can access its liberated state.
        </p>

        <h3 style={head}>The Emotional Spectrum</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 14px' }}>
          <span style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#4AAE88' }}>Liberated</span>
          <div style={{ flex: 1, height: '3px', borderRadius: '2px', background: 'linear-gradient(90deg, #4AAE88, #C87878)' }} />
          <span style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#C87878' }}>Burden</span>
        </div>
        <div style={{ ...esmGrid, paddingBottom: '8px', borderBottom: '1px solid rgba(142,196,224,0.15)' }}>
          {['Dimension', 'Liberated', 'Burden', 'Fundamental Right'].map((h, i) => (
            <span key={i} style={{ fontSize: '8px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#8BAFC8' }}>{h}</span>
          ))}
        </div>
        {esmRows.map((r, i) => (
          <div key={i} style={{ ...esmGrid, padding: '9px 0', borderBottom: i < esmRows.length - 1 ? '1px solid rgba(142,196,224,0.07)' : 'none' }}>
            <span style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#A0C4D8' }}>{r[0]}</span>
            <span style={{ fontSize: '13px', color: '#4AAE88', fontFamily: 'Georgia, serif' }}>{r[1]}</span>
            <span style={{ fontSize: '13px', color: '#C87878', fontFamily: 'Georgia, serif' }}>{r[2]}</span>
            <span style={{ fontSize: '11px', fontStyle: 'italic', color: '#8BAFC8' }}>{r[3]}</span>
          </div>
        ))}

        <p style={{ ...para, marginTop: '22px' }}>
          A complex is composed of beliefs, thoughts, and feelings, which receive their input from the emotion. In its attempt to regulate, the complex needs an output, expressed through behaviors. Triggers activate the complex.
        </p>

        <h3 style={head}>Anatomy of a Complex</h3>
        <div style={{ overflowX: 'auto', padding: '6px 0 2px' }}>
          <SpineRow label="Emotional Burden" color="#C87878" border="rgba(176,90,90,0.4)" bg="rgba(176,90,90,0.08)" note="← the root wound" />
          <Down />
          <SpineRow label="Beliefs" color="#8EC4E0" border="rgba(142,196,224,0.3)" bg="rgba(142,196,224,0.05)" counter="Counter Belief" note="← emotional input" />
          <Down />
          <SpineRow label="Thoughts" color="#8EC4E0" border="rgba(142,196,224,0.3)" bg="rgba(142,196,224,0.05)" />
          <Down />
          <SpineRow label="Feelings" color="#8EC4E0" border="rgba(142,196,224,0.3)" bg="rgba(142,196,224,0.05)" />
          <Down />
          <SpineRow label="Behaviors" color="#8EC4E0" border="rgba(142,196,224,0.3)" bg="rgba(142,196,224,0.05)" counter="Counter Behavior" note="← emotional output" />
          <Up />
          <SpineRow label="Trigger" color="#C8A840" border="rgba(200,168,80,0.4)" bg="rgba(200,168,80,0.06)" />
        </div>

        <p style={{ ...para, marginTop: '22px' }}>
          Mapping a complex, and building the map of complexes, is what allows you to dissolve them.
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button style={{ background: 'rgba(142,196,224,0.15)', border: '1px solid rgba(142,196,224,0.4)', borderRadius: '3px', padding: '10px 24px', color: '#8EC4E0', fontSize: '11px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' }} onClick={onClose}>Close</button>
        </div>

      </div>
    </div>
  );
}

function BuildHowItWorksModal({ onClose }) {
  const para = { fontSize: '13px', lineHeight: 1.75, color: '#B3C9DA', margin: '0 0 14px' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 20px' }} onClick={onClose}>
      <div style={{ background: '#162534', border: '1px solid rgba(142,196,224,0.3)', borderRadius: '4px', width: '100%', maxWidth: '720px', padding: '32px', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '4px' }}>
          <div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', fontWeight: '300', color: '#D8E6F0' }}>How It Works</div>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase', color: '#8BAFC8', marginTop: '4px' }}>Build Complex</div>
          </div>
          <button style={{ background: 'none', border: 'none', color: '#8BAFC8', cursor: 'pointer', fontSize: '18px' }} onClick={onClose}>✕</button>
        </div>
        <p style={{ ...para, marginTop: '18px' }}>
          Choose Custom to build a complex manually, entering each part yourself. Choose Guided to be walked through it: pick an entry point and the questionnaire leads you the rest of the way. If you are triggered or dysregulated in the moment, use Guided and start from whichever entry point is clearest to you right now, the trigger, the emotion, the behavior, or the belief.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button style={{ background: 'rgba(142,196,224,0.15)', border: '1px solid rgba(142,196,224,0.4)', borderRadius: '3px', padding: '10px 24px', color: '#8EC4E0', fontSize: '11px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' }} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function CPM() {

  const location = useLocation();
  const token = localStorage.getItem('axis_token');
  const initialTab = (() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('view');
    return v === 'tree' ? 'tree' : 'emotion';
  })();
  const initialCategory = (() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('category') || '';
  })();
  const [complexes, setComplexes] = useState([]);
  const [dreams, setDreams] = useState([]);
  const [people, setPeople] = useState([]);
  const [patternCategories, setPatternCategories] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(initialCategory);
  const [treeOrder, setTreeOrder] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [showInfo, setShowInfo] = useState(false);
  const [filter, setFilter] = useState('active');
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderMode, setBuilderMode] = useState('guided');
  const [prefillBehavior, setPrefillBehavior] = useState(null);
  const [editIdx, setEditIdx] = useState(null);
  const [viewIdx, setViewIdx] = useState(null);
  const [resolutionFormIdx, setResolutionFormIdx] = useState(null);
  const [resolutionViewComplexes, setResolutionViewComplexes] = useState(null);
  const [form, setForm] = useState({ name: '', burden: '', beliefs: '', thoughts: '', feelings: '', behaviors: '', trigger: '', counter: '', counterBehavior: '', notes: '', period: [], source: '', rootComplex: [], status: 'active', originalWound: false });
  const [saving, setSaving] = useState(false);
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    loadComplexes();
    if (location.state && location.state.buildFromBehavior) {
      setPrefillBehavior(location.state.buildFromBehavior); setShowBuilder(true); setBuilderMode('guided'); setEditIdx(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadComplexes = async () => {
    try {
      const [complexRes, dreamsRes, peopleRes, catsRes, patternsRes] = await Promise.all([
        axios.get(API + '/api/complexes', { headers: { Authorization: 'Bearer ' + token } }),
        axios.get(API + '/api/dreams', { headers: { Authorization: 'Bearer ' + token } }),
        axios.get(API + '/api/people', { headers: { Authorization: 'Bearer ' + token } }),
        axios.get(API + '/api/pattern-categories', { headers: { Authorization: 'Bearer ' + token } }),
        axios.get(API + '/api/patterns', { headers: { Authorization: 'Bearer ' + token } }),
      ]);
      setComplexes(complexRes.data || []);
      setDreams(dreamsRes.data || []);
      setPeople(peopleRes.data || []);
      setPatternCategories(catsRes.data || []);
      setPatterns(patternsRes.data || []);
      try {
        const orderRes = await axios.get(API + '/api/tree-order', { headers: { Authorization: 'Bearer ' + token } });
        if (orderRes.data) setTreeOrder(orderRes.data);
      } catch (e) { }
    } catch (err) { console.log(err); }
    finally { setLoading(false); }
  };

  const savePerson = async (name) => {
    if (!name || !name.trim()) return;
    const trimmed = name.trim();
    if (people.find(p => p.name === trimmed)) return;
    const updated = [...people, { name: trimmed }];
    await axios.post(API + '/api/people', { data: updated }, { headers: { Authorization: 'Bearer ' + token } });
    setPeople(updated);
  };

  const saveTreeOrder = async (order) => {
    try {
      await axios.post(API + '/api/tree-order', { data: order }, { headers: { Authorization: 'Bearer ' + token } });
      setTreeOrder(order);
    } catch (e) { console.log(e); }
  };

  const saveComplex = async (data) => {
    const payload = { ...data };
    if (!payload.name || !payload.name.trim()) return;
    if (typeof payload.rootComplex === 'string') payload.rootComplex = payload.rootComplex ? [payload.rootComplex] : [];
    if (!Array.isArray(payload.rootComplex)) payload.rootComplex = [];
    if (payload.originalWound) payload.rootComplex = [];
    setSaving(true);
    try {
      let updated;
      if (editIdx !== null) { updated = [...complexes]; updated[editIdx] = payload; }
      else { updated = [...complexes, payload]; }
      await axios.post(API + '/api/complexes', { data: updated }, { headers: { Authorization: 'Bearer ' + token } });
      setComplexes(updated); closeBuilder();
    } catch (err) { console.log(err); }
    finally { setSaving(false); }
  };

  const markResolved = async (idx, resolutionData) => {
    const updated = [...complexes];
    updated[idx] = { ...updated[idx], status: 'resolved', resolution: resolutionData };
    await axios.post(API + '/api/complexes', { data: updated }, { headers: { Authorization: 'Bearer ' + token } });
    setComplexes(updated); setResolutionFormIdx(null);
  };

  const deleteComplex = async (idx) => {
    const name = complexes[idx] && complexes[idx].name ? complexes[idx].name : 'this complex';
    if (!window.confirm('Delete "' + name + '"? This cannot be undone.')) return;
    const updated = complexes.filter((_, i) => i !== idx);
    await axios.post(API + '/api/complexes', { data: updated }, { headers: { Authorization: 'Bearer ' + token } });
    setComplexes(updated); if (viewIdx === idx) setViewIdx(null);
  };

  const toggleStatus = async (idx, status) => {
    if (status === 'resolved') { setResolutionFormIdx(idx); return; }
    const updated = [...complexes];
    updated[idx] = { ...updated[idx], status, resolution: null };
    await axios.post(API + '/api/complexes', { data: updated }, { headers: { Authorization: 'Bearer ' + token } });
    setComplexes(updated);
  };

  const openBuilder = (idx = null) => {
    setPrefillBehavior(null);
    if (idx !== null) {
      const c = complexes[idx];
      setForm({ ...c, rootComplex: c.rootComplex || [], source: c.source || '', period: Array.isArray(c.period) ? c.period : (c.period ? [c.period] : []) });
      setEditIdx(idx); setBuilderMode('custom');
    } else {
      setForm({ name: '', burden: '', beliefs: '', thoughts: '', feelings: '', behaviors: '', trigger: '', counter: '', counterBehavior: '', notes: '', period: [], source: '', rootComplex: [], status: 'active', originalWound: false });
      setEditIdx(null); setBuilderMode('guided');
    }
    setShowBuilder(true);
  };

  const closeBuilder = () => { setShowBuilder(false); setEditIdx(null); setPrefillBehavior(null); };

  const groups = {};
  EMOTION_ORDER.forEach(e => { groups[e] = []; });
  const filtered = complexes.filter(c => { if (filter === 'active') return c.status === 'active'; if (filter === 'resolved') return c.status === 'resolved'; return true; });
  filtered.forEach(c => {
    const realIdx = complexes.indexOf(c);
    const b = c.burden ? (c.burden.charAt(0).toUpperCase() + c.burden.slice(1)) : null;
    const key = b && groups[b] !== undefined ? b : null;
    if (key) groups[key].push({ ...c, _idx: realIdx });
  });

  const availableRoots = complexes.filter((c, i) => i !== editIdx && c.status !== 'resolved');
  const isInnerChildForm = Array.isArray(form.period) ? (form.period.includes('Early Childhood') || form.period.includes('Childhood')) : (form.period === 'Early Childhood' || form.period === 'Childhood');

  if (loading) return <div style={{ color: '#8BAFC8', padding: '48px', textAlign: 'center' }}>Loading...</div>;

  if (showBuilder) {
    const isEdit = editIdx !== null;
    return (
      <Page>
        {showInfo && <BuildHowItWorksModal onClose={() => setShowInfo(false)} />}
        <AppHeader backLabel="← Cancel" onBack={closeBuilder} title={isEdit ? 'Edit Complex' : 'Build Complex'} />
        <PageBody width="reading">
          {!isEdit && !prefillBehavior && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <button style={styles.infoTrigger} onClick={() => setShowInfo(true)}>
                  <span style={styles.infoTriggerIcon}>i</span> How it works
                </button>
              </div>
              <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(142,196,224,0.15)', marginBottom: '36px' }}>
                {['guided', 'custom'].map(m => <button key={m} style={{ ...styles.tabBtn, ...(builderMode === m ? styles.tabBtnActive : {}) }} onClick={() => setBuilderMode(m)}>{m.charAt(0).toUpperCase() + m.slice(1)}</button>)}
              </div>
            </>
          )}
          {(builderMode === 'guided' && !isEdit) && <GuidedBuilder onSave={saveComplex} complexes={complexes} prefillBehavior={prefillBehavior} />}
          {(builderMode === 'custom' || isEdit) && (
            <div style={styles.card}>
              <div style={styles.formGroup}><label style={styles.label}>Name</label><input style={styles.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Give this complex a name..." autoFocus /></div>
              <div style={styles.formGroup}><label style={styles.label}>Underlying Emotional Burden</label><select style={styles.input} value={form.burden} onChange={e => setForm({ ...form, burden: e.target.value })}><option value="">-- Select burden --</option>{EMOTION_ORDER.map(e => <option key={e} value={e}>{e}</option>)}</select></div>
              <div style={styles.formGroup}><label style={styles.label}>Beliefs <ArrowLeft /></label><textarea style={styles.textarea} value={form.beliefs} onChange={e => setForm({ ...form, beliefs: e.target.value })} placeholder="What are beliefs you hold while this complex is active?" rows={3} /></div>
              <div style={styles.formGroup}><label style={styles.label}>Thoughts <ArrowLeft /></label><textarea style={styles.textarea} value={form.thoughts} onChange={e => setForm({ ...form, thoughts: e.target.value })} placeholder="What thoughts go through your mind while this complex is active?" rows={3} /></div>
              <div style={styles.formGroup}><label style={styles.label}>Feelings <ArrowLeft /></label><textarea style={styles.textarea} value={form.feelings} onChange={e => setForm({ ...form, feelings: e.target.value })} placeholder="What do you feel while this complex is active?" rows={3} /></div>
              <div style={styles.formGroup}><label style={styles.label}>Behaviors <ArrowRight /></label><textarea style={styles.textarea} value={form.behaviors} onChange={e => setForm({ ...form, behaviors: e.target.value })} placeholder="How do you act while this complex is active?" rows={3} /></div>
              <div style={styles.formGroup}><label style={styles.label}>Trigger</label><input style={styles.input} value={form.trigger} onChange={e => setForm({ ...form, trigger: e.target.value })} placeholder="What activates this complex?" /></div>
              <div style={styles.formGroup}>
                <label style={{ ...styles.label, color: '#4AAE88' }}>Counter Belief</label>
                {isInnerChildForm && <div style={{ fontSize: '11px', fontStyle: 'italic', color: 'rgba(200,168,80,0.6)', marginBottom: '8px' }}>You are speaking to your inner child.</div>}
                <textarea style={{ ...styles.textarea, borderColor: 'rgba(74,174,136,0.2)' }} value={form.counter} onChange={e => setForm({ ...form, counter: e.target.value })} placeholder="What else could also be true?" rows={3} />
              </div>
              <div style={styles.formGroup}>
                <label style={{ ...styles.label, color: '#4AAE88' }}>Counter Behavior</label>
                {isInnerChildForm && <div style={{ fontSize: '11px', fontStyle: 'italic', color: 'rgba(200,168,80,0.6)', marginBottom: '8px' }}>You are speaking to your inner child.</div>}
                <textarea style={{ ...styles.textarea, borderColor: 'rgba(74,174,136,0.2)' }} value={form.counterBehavior} onChange={e => setForm({ ...form, counterBehavior: e.target.value })} placeholder="What would be alternative behaviors?" rows={3} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Life Period <span style={{ fontStyle: 'italic', fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: '6px' }}>optional, select all that apply</span></label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {LIFE_PERIODS.map(p => {
                    const periods = Array.isArray(form.period) ? form.period : (form.period ? [form.period] : []);
                    const isSelected = periods.includes(p.label);
                    return <button key={p.label} style={{ background: isSelected ? 'rgba(142,196,224,0.12)' : 'rgba(142,196,224,0.03)', border: '1px solid ' + (isSelected ? 'rgba(142,196,224,0.6)' : 'rgba(142,196,224,0.15)'), borderRadius: '3px', padding: '10px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => { const current = Array.isArray(form.period) ? form.period : (form.period ? [form.period] : []); const updated = current.includes(p.label) ? current.filter(x => x !== p.label) : [...current, p.label]; const sorted = LIFE_PERIODS.filter(lp => updated.includes(lp.label)).map(lp => lp.label); setForm({ ...form, period: sorted }); }}><div style={{ fontSize: '12px', fontWeight: '600', color: isSelected ? '#D8E6F0' : '#A0C4D8' }}>{p.label}</div><div style={{ fontSize: '10px', color: '#8BAFC8', marginTop: '2px' }}>{p.range}</div></button>;
                  })}
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Person <span style={{ fontStyle: 'italic', fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: '6px' }}>optional, who is this complex about?</span></label>
                <PersonPicker value={form.person || ''} onChange={(name) => setForm({ ...form, person: name })} people={people} onAddPerson={savePerson} />
              </div>
              <div style={{ ...styles.woundToggle, ...(form.originalWound ? styles.woundActive : {}) }} onClick={() => {
                const newVal = !form.originalWound;
                setForm({ ...form, originalWound: newVal, rootComplex: newVal ? [] : form.rootComplex });
              }}>
                <div style={{ ...styles.woundDot, ...(form.originalWound ? styles.woundDotActive : {}) }} />
                <div><div style={styles.woundLabel}>Original Wound</div><div style={styles.woundDesc}>Mark this complex as the origin, where the pattern first formed.</div></div>
              </div>
              <div style={{ marginBottom: '20px', padding: '20px', border: '1px solid rgba(142,196,224,0.15)', borderRadius: '4px', background: 'rgba(142,196,224,0.02)' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', color: '#8EC4E0', marginBottom: '4px' }}>Placement on the Tree</div>
                <div style={{ fontSize: '11px', color: '#8BAFC8', fontStyle: 'italic', lineHeight: 1.5, marginBottom: '18px' }}>These fields decide where this complex sits in your tree. Both optional.</div>

                <div style={{ marginBottom: form.originalWound ? 0 : '20px' }}>
                  <label style={styles.label}>Source <span style={{ fontStyle: 'italic', fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: '6px' }}>which column it sits under</span></label>
                  <select
                    style={styles.input}
                    value={form.source && SOURCES.includes(form.source) && form.source !== 'Custom' ? form.source : (form.source ? 'Custom' : '')}
                    onChange={e => {
                      if (e.target.value === 'Custom') setForm({ ...form, source: 'Custom' });
                      else setForm({ ...form, source: e.target.value });
                    }}
                  >
                    <option value="">None / Unknown</option>
                    {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {(form.source === 'Custom' || (form.source && !SOURCES.includes(form.source))) && (
                    <input
                      style={{ ...styles.input, marginTop: '8px' }}
                      value={form.source === 'Custom' ? '' : form.source}
                      onChange={e => setForm({ ...form, source: e.target.value || 'Custom' })}
                      placeholder="Type the source (e.g., 'Aunt Marie', 'High School Coach')..."
                    />
                  )}
                </div>

                {!form.originalWound && (
                  <div style={{ marginBottom: 0 }}>
                    <label style={styles.label}>Root Complex <span style={{ fontStyle: 'italic', fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: '6px' }}>which complex it grows from</span></label>
                    <RootComplexSelector value={form.rootComplex} onChange={(updated) => setForm({ ...form, rootComplex: updated })} availableRoots={availableRoots} />
                  </div>
                )}
              </div>
              <div style={styles.formGroup}><label style={styles.label}>Notes</label><textarea style={styles.textarea} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional observations..." rows={3} /></div>
              <div style={styles.formFooter}>
                <button style={styles.cancelBtn} onClick={closeBuilder}>Cancel</button>
                <button style={styles.btn} onClick={() => saveComplex(form)} disabled={saving}>{saving ? 'Saving...' : 'Save to Map'}</button>
              </div>
            </div>
          )}
        </PageBody>
      </Page>
    );
  }

  if (tab === 'tree') {
    return (
      <Page>
        {showInfo && <HowItWorksModal onClose={() => setShowInfo(false)} />}
        {viewIdx !== null && <ViewModal complex={complexes[viewIdx]} dreams={dreams} people={people} complexes={complexes} onClose={() => setViewIdx(null)} onEdit={() => { openBuilder(viewIdx); setViewIdx(null); }} />}
        {resolutionViewComplexes && <ResolutionModal resolvedComplexes={resolutionViewComplexes} onClose={() => setResolutionViewComplexes(null)} />}
        {resolutionFormIdx !== null && <ResolutionForm complex={complexes[resolutionFormIdx]} onSave={(data) => markResolved(resolutionFormIdx, data)} onCancel={() => setResolutionFormIdx(null)} />}
        <AppHeader
          title="Complex Pattern Map"
          right={<button style={styles.btn} onClick={() => openBuilder()}>+ Build Complex</button>}
        />
        {!maximized && (<>
        <div style={{ padding: '16px 32px 0', flexShrink: 0 }}>
          <button style={styles.infoTrigger} onClick={() => setShowInfo(true)}>
            <span style={styles.infoTriggerIcon}>i</span> How it works
          </button>
        </div>
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(142,196,224,0.15)', padding: '0 32px', flexShrink: 0 }}>
          {[{ id: 'emotion', label: 'By Emotion' }, { id: 'tree', label: 'Tree View' }].map(t => (
            <button key={t.id} style={{ ...styles.tabBtn, ...(tab === t.id ? styles.tabBtnActive : {}) }} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>
        </>)}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 32px', borderBottom: '1px solid rgba(142,196,224,0.08)', flexShrink: 0 }}>
          <span style={{ fontSize: '9px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#8BAFC8' }}>View by:</span>
          <select
            style={{ background: '#0f2236', border: '1px solid rgba(142,196,224,0.2)', borderRadius: '3px', padding: '6px 10px', color: '#D8E6F0', fontSize: '12px', outline: 'none', cursor: 'pointer' }}
            value={activeCategoryId}
            onChange={e => setActiveCategoryId(e.target.value)}
          >
            <option value="">None</option>
            {patternCategories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          {activeCategoryId && (() => {
            const activePatterns = patterns.filter(p => p.categoryId === activeCategoryId);
            if (activePatterns.length === 0) return null;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginLeft: '16px', flexWrap: 'wrap' }}>
                {activePatterns.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                    <span style={{ fontSize: '11px', color: '#A0C4D8' }}>{p.name}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
        <PageBody width="full">
          <TreeView complexes={complexes} people={people} patterns={patterns} activeCategoryId={activeCategoryId} maximized={maximized} onToggleMax={() => setMaximized(m => !m)} onViewComplex={(idx) => setViewIdx(idx)} onViewResolution={(cxs) => setResolutionViewComplexes(cxs)} onSaveOrder={saveTreeOrder} savedOrder={treeOrder} />
        </PageBody>
      </Page>
    );
  }

  return (
    <Page>
      {showInfo && <HowItWorksModal onClose={() => setShowInfo(false)} />}
      {viewIdx !== null && <ViewModal complex={complexes[viewIdx]} dreams={dreams} people={people} complexes={complexes} onClose={() => setViewIdx(null)} onEdit={() => { openBuilder(viewIdx); setViewIdx(null); }} />}
      {resolutionFormIdx !== null && <ResolutionForm complex={complexes[resolutionFormIdx]} onSave={(data) => markResolved(resolutionFormIdx, data)} onCancel={() => setResolutionFormIdx(null)} />}
      <AppHeader
        title="Complex Pattern Map"
        right={<button style={styles.btn} onClick={() => openBuilder()}>+ Build Complex</button>}
      />
      <PageBody width="content">
        <div style={{ marginBottom: '16px' }}>
          <button style={styles.infoTrigger} onClick={() => setShowInfo(true)}>
            <span style={styles.infoTriggerIcon}>i</span> How it works
          </button>
        </div>
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(142,196,224,0.15)', marginBottom: '24px' }}>
          {[{ id: 'emotion', label: 'By Emotion' }, { id: 'tree', label: 'Tree View' }].map(t => (
            <button key={t.id} style={{ ...styles.tabBtn, ...(tab === t.id ? styles.tabBtnActive : {}) }} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>
        {tab === 'emotion' && (
          <>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
              {['all', 'active', 'resolved'].map(f => <button key={f} style={{ ...styles.filterTab, ...(filter === f ? styles.filterTabActive : {}) }} onClick={() => setFilter(f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>)}
            </div>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 24px', fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#8BAFC8' }}>{filter === 'all' ? 'No complexes yet. Build your first complex pattern to begin mapping your inner world.' : 'No ' + filter + ' complexes.'}</div>
            ) : (
              EMOTION_ORDER.map(emotion => {
                const group = groups[emotion]; if (!group || group.length === 0) return null;
                const col = EMOTION_COLORS[emotion];
                return (
                  <div key={emotion} style={{ marginBottom: '40px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '32px 0 16px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: col.text, flexShrink: 0 }} />
                      <span style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase', color: col.text }}>{emotion}</span>
                      <div style={{ flex: 1, height: '1px', background: col.border, opacity: 0.4 }} />
                      <span style={{ fontSize: '10px', color: '#8BAFC8' }}>{group.length} complex{group.length > 1 ? 'es' : ''}</span>
                    </div>
                    <div style={styles.complexGrid}>
                      {group.map(c => (
                        <div key={c._idx} style={{ ...styles.complexCard, borderLeftColor: c.status === 'resolved' ? 'rgba(74,174,136,0.5)' : col.border, background: c.status === 'resolved' ? 'rgba(74,174,136,0.05)' : col.bg, opacity: c.status === 'resolved' ? 0.8 : 1, ...(c.originalWound ? { borderStyle: 'dashed', borderLeftStyle: 'solid', borderWidth: '2px', borderLeftWidth: '3px' } : {}) }} onClick={(e) => { if (!e.target.closest('button')) setViewIdx(c._idx); }}>
                          <div style={styles.complexCardHeader}>
                            <div style={{ ...styles.complexCardName, color: c.status === 'resolved' ? '#4AAE88' : '#D8E6F0' }}>{c.name}{c.originalWound && <span style={{ color: '#C8A840', fontSize: '14px' }}> ◉</span>}</div>
                            {c.originalWound && <span style={{ fontSize: '8px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: col.text, border: '1px dashed ' + col.border, padding: '2px 7px', borderRadius: '2px', whiteSpace: 'nowrap' }}>Original Wound</span>}
                            {c.status === 'resolved' && <span style={{ fontSize: '8px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#4AAE88', border: '1px solid rgba(74,174,136,0.4)', padding: '2px 7px', borderRadius: '2px', whiteSpace: 'nowrap' }}>Resolved</span>}
                          </div>
                          <div style={{ ...styles.cardBurden, color: c.status === 'resolved' ? 'rgba(74,174,136,0.7)' : col.text }}>{c.burden}</div>
                          {c.period && <div style={{ fontSize: '9px', color: '#8BAFC8', marginBottom: '4px', letterSpacing: '1px' }}>{getPeriodDisplay(c.period)}</div>}
                          {c.source && <div style={{ fontSize: '9px', color: '#8BAFC8', marginBottom: '6px', letterSpacing: '1px' }}>{c.source}</div>}
                          <div style={styles.cardFooter}>
                            <button style={styles.smallBtn} onClick={e => { e.stopPropagation(); openBuilder(c._idx); }}>Edit</button>
                            {c.status === 'active' ? <button style={styles.smallBtn} onClick={e => { e.stopPropagation(); toggleStatus(c._idx, 'resolved'); }}>Mark Resolved</button> : <button style={styles.smallBtn} onClick={e => { e.stopPropagation(); toggleStatus(c._idx, 'active'); }}>Reopen</button>}
                            <button style={{ ...styles.smallBtn, color: '#C87878', borderColor: 'rgba(176,90,90,0.3)', marginLeft: 'auto' }} onClick={e => { e.stopPropagation(); deleteComplex(c._idx); }}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </PageBody>
    </Page>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#0d1b2a', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 32px', borderBottom: '1px solid rgba(142,196,224,0.15)', background: '#0f2236', flexShrink: 0 },
  backBtn: { background: 'none', border: 'none', color: '#8BAFC8', fontSize: '12px', fontWeight: '600', letterSpacing: '1px', cursor: 'pointer', padding: 0 },
  title: { fontFamily: 'Georgia, serif', fontSize: '24px', fontWeight: '300', color: '#D8E6F0', flex: 1 },
  toolbarTitle: { fontSize: '11px', fontWeight: '600', letterSpacing: '4px', textTransform: 'uppercase', color: '#8BAFC8', flex: 1, textAlign: 'center' },
  btn: { background: 'rgba(142,196,224,0.15)', border: '1px solid rgba(142,196,224,0.4)', borderRadius: '3px', padding: '10px 20px', color: '#8EC4E0', fontSize: '11px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' },
  tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '12px 20px', color: '#8BAFC8', fontSize: '10px', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase', cursor: 'pointer', marginBottom: '-1px' },
  tabBtnActive: { color: '#D8E6F0', borderBottomColor: '#8EC4E0' },
  filterTab: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', padding: '6px 16px', border: '1px solid rgba(142,196,224,0.2)', background: 'none', color: '#8BAFC8', cursor: 'pointer', borderRadius: '2px' },
  filterTabActive: { background: 'rgba(142,196,224,0.1)', borderColor: 'rgba(142,196,224,0.5)', color: '#8EC4E0' },
  complexGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
  complexCard: { border: '1px solid rgba(142,196,224,0.1)', borderLeft: '3px solid', borderRadius: '3px', padding: '14px 16px', cursor: 'pointer', transition: 'opacity 0.2s' },
  complexCardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '6px' },
  complexCardName: { fontFamily: 'Georgia, serif', fontSize: '15px', fontWeight: '300', color: '#D8E6F0', lineHeight: 1.4 },
  cardBurden: { fontSize: '9px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '4px' },
  cardFooter: { display: 'flex', gap: '6px', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(142,196,224,0.08)' },
  smallBtn: { background: 'none', border: '1px solid rgba(142,196,224,0.2)', borderRadius: '2px', padding: '4px 10px', color: '#8BAFC8', fontSize: '10px', cursor: 'pointer' },
  card: { background: '#162534', border: '1px solid rgba(142,196,224,0.15)', borderRadius: '4px', padding: '32px' },
  formGroup: { marginBottom: '20px' },
  label: { display: 'flex', alignItems: 'center', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#8BAFC8', marginBottom: '8px' },
  input: { width: '100%', background: '#0f2236', border: '1px solid rgba(142,196,224,0.2)', borderRadius: '3px', padding: '10px 14px', color: '#D8E6F0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', background: '#0f2236', border: '1px solid rgba(142,196,224,0.2)', borderRadius: '3px', padding: '10px 14px', color: '#D8E6F0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
  woundToggle: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', border: '1px solid rgba(200,168,80,0.2)', borderRadius: '3px', background: 'rgba(200,168,80,0.03)', cursor: 'pointer', marginBottom: '20px' },
  woundActive: { borderColor: 'rgba(200,168,80,0.5)', background: 'rgba(200,168,80,0.07)' },
  woundDot: { width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(200,168,80,0.4)', flexShrink: 0 },
  woundDotActive: { background: 'rgba(200,168,80,0.8)', borderColor: 'rgba(200,168,80,0.9)' },
  woundLabel: { fontSize: '9px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(200,168,80,0.7)' },
  woundDesc: { fontSize: '11px', color: '#8BAFC8', marginTop: '2px' },
  formFooter: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' },
  cancelBtn: { background: 'none', border: '1px solid rgba(142,196,224,0.2)', borderRadius: '3px', padding: '10px 20px', color: '#8BAFC8', fontSize: '11px', cursor: 'pointer' },
  treeCtrlBtn: { background: 'none', border: '1px solid rgba(142,196,224,0.2)', color: '#8BAFC8', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', borderRadius: '2px', padding: 0 },
  infoTrigger: { display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(142,196,224,0.06)', border: '1px solid rgba(142,196,224,0.3)', borderRadius: '20px', padding: '7px 16px', color: '#8EC4E0', fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' },
  infoTriggerIcon: { width: '15px', height: '15px', borderRadius: '50%', border: '1px solid rgba(142,196,224,0.5)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '10px', lineHeight: 1 },
};

export default CPM;